






















































import { defineComponent, reactive, SetupContext, toRefs, onMounted, ref, computed } from "@vue/composition-api";
import http from "../../plugins/http";
import JSZip from 'jszip';
import DropArea from "../../components/DropArea.vue";
import AlertArea from "../../components/AlertArea.vue";
import { BModal } from 'bootstrap-vue';
import PdfRenderArea from "../../components/PdfRenderArea.vue"
import { SearchCondition, PracticeExaminee } from "../../modules/teacher/PracticePdfList"
import { zipObject } from "lodash";

interface Data {
  condition: SearchCondition
  practiceExaminees: PracticeExaminee[]
  modalExaminee: PracticeExaminee
  sumup_group_id: string
  fields: any[],
  isAllCheckedDelete: boolean
  isDispProgress: boolean
  progress: string
  windowHeight: number
  base64Pdf: string
  errors: string[]
}

export default defineComponent({
  components: {
    "alert-area": AlertArea,
    "drop-area": DropArea,
    "pdf-render-area": PdfRenderArea,
  },
  setup(props, context: SetupContext) {

    /******************************
     * ref
     ******************************/

    const modalRef = ref<InstanceType<typeof BModal>>();
    const pdfRenderAreaRef = ref<InstanceType<typeof PdfRenderArea>>();

    /******************************
     * data
     ******************************/

    const state = reactive<Data>({
      condition: new SearchCondition(),
      practiceExaminees: [],
      modalExaminee: null,
      sumup_group_id: null,
      fields: [
        {key: "student_no_character", label: "受験番号", sortable: true, class: 'text-center b-table-th-td'},
        {key: "student_name", label: "氏名", sortable: true, class: 'text-center b-table-th-td'},
        {key: "pdf_uploaded_at", label: "アップロード日時", sortable: true, class: 'text-center b-table-th-td'},
        {key: "pdf_image", label: "PDF表示", sortable: true, class: 'text-center b-table-th-td'},
        {key: "isSelected", label: "削除", sortable: false, class: 'text-center b-table-th-td'},
      ],
      isAllCheckedDelete: false,
      isDispProgress: false,
      progress: "0.0",
      windowHeight: 0,
      base64Pdf: "",
      errors: [],
    });

    /******************************
     * 成績表モーダル表示
     ******************************/

    const previewImage = async (practiceExaminee: PracticeExaminee) => {
      if(!practiceExaminee.base64Pdf){
        await download(practiceExaminee);
      }
      state.modalExaminee = practiceExaminee;
      modalRef.value.show();
    }

    /******************************
     * 成績表モーダル表示後の画像表示処理
     ******************************/

    const modalShown = () => {
      state.base64Pdf = state.modalExaminee.base64Pdf;
    }

    /******************************
     * 成績表モーダルクローズ
     ******************************/

    const modalHidden = () => {
      state.base64Pdf = null;
    }
    
    /******************************
     * 戻るアイコン押下
     ******************************/

    const goBack = (): void => {
        context.root.$router.go(-1);
    };

    /******************************
     * アップロード
     ******************************/

    const upload = async (file: File): Promise<void> => {

      state.errors = [];

      await JSZip.loadAsync(file).then(async zip => {
        const zipObjects = zip.file(/\d*\.(pdf)/).filter(x => x.name.includes(".pdf") && !x.name.includes("__MACOSX"));
        
        if(zipObject.length == 0){
          state.errors.push("Zip内にアップロード対象のPDFファイルが存在しません。");
          return;
        }

        if(!window.confirm("ファイルをアップロードします。\n実行してよろしいですか？")){
          return;
        }

        context.root.$store.dispatch("showOverlay", "転送中。画面を閉じずにそのままお待ちください。途中で画面を閉じると処理が中断されます。");

        state.isDispProgress = true;
        state.progress = "0.00";
        let completedCount = 0;
        
        for(const zipObject of zipObjects){
          await zipObject.async("blob").then(async blob => {
            let fd = new FormData();
            fd.append('upload[file]', blob);
            fd.append('student_no_character', zipObject.name.split("/").reverse()[0].split('.')[0].split(".")[0]);
            await http.post(`/api/practice_pdf_list/upload/${state.sumup_group_id}`, fd).then(response => {
              completedCount++;
              state.progress = Number.parseFloat(`${completedCount * 100 / zipObjects.length}`).toFixed(2);
            })
            .catch(error=>{
              if(error.response.status == 422){
                state.errors = error.response.data.errors;
              }else{
                state.errors = ["予期せぬ例外が発生しました。失敗した生徒は再度アップロードを行ってください。"];
              }
            });
          });

          // エラーありの場合は処理終了
          if(state.errors.length > 0){
            context.root.$store.dispatch("hideOverlay");
            state.isDispProgress = false;
            await search();
            return;
          }

          await resolveAfterMilliSeconds();
        }

        state.progress = "100.00";
        await resolveAfterMilliSeconds();
        alert("処理が完了しました。");
        state.isDispProgress = false;
        context.root.$store.dispatch("hideOverlay");

        await search();
      });
    }

    const resolveAfterMilliSeconds = () => {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve('resolved');
        }, 1);
      });
    }

    /******************************
     * ダウンロード
     ******************************/

    const download = async (practiceExaminee: PracticeExaminee) => {
      context.root.$store.dispatch("showOverlay", "データ取得中... しばらくお待ちください。");
      
      await http.get(`/api/practice_pdf_list/download/${state.sumup_group_id}?student_no_character=${practiceExaminee.student_no_character}`).then(response => {
        practiceExaminee.base64Pdf = response.data.base64_pdf;
      })
      .catch(error=>{
        if(error.response.status == 422){
          state.errors = error.response.data.errors;
        }else{
          state.errors = ["予期せぬ例外が発生しました。"];
        }
      });

      context.root.$store.dispatch("hideOverlay");
    }

    /******************************
     * 検索
     ******************************/

    const search = async () => {
      context.root.$store.dispatch("showOverlay", "データ取得中... しばらくお待ちください。");
      
      await http.get(`/api/practice_pdf_list/?sumup_group_id=${state.sumup_group_id}`).then(response => {
        state.practiceExaminees = response.data.practice_examinees.map((x: any) => new PracticeExaminee(x));
        state.modalExaminee = state.practiceExaminees[0];
      })
      .catch(error=>{
        if(error.response.status == 422){
          state.errors = error.response.data.errors;
        }else{
          state.errors = ["予期せぬ例外が発生しました。"];
        }
      });

      context.root.$store.dispatch("hideOverlay");
    }

    /******************************
     * PDF削除
     ******************************/

    const destroyPdf = async () => {

      const student_no_characters = state.practiceExaminees.filter(x => x.isSelected).map(x => x.student_no_character);

      if(student_no_characters.length == 0){
        state.errors.push("削除対象を選択してください。");
        return;
      }

      if(!window.confirm("成績表PDFを削除します。\nよろしいですか？")){
        return;
      }

      context.root.$store.dispatch("showOverlay");
      
      await http.post(`/api/practice_pdf_list/destroy_pdf/${state.sumup_group_id}`, {student_no_characters: student_no_characters}).then(response => {
        alert("処理が完了しました。");
      })
      .catch(error=>{
        if(error.response.status == 422){
          state.errors = error.response.data.errors;
        }else{
          state.errors = ["予期せぬ例外が発生しました。"];
        }
      });

      await search();

      context.root.$store.dispatch("hideOverlay");
    }

    /******************************
     * mounted
     ******************************/

    onMounted(async () => {
      state.sumup_group_id = context.root.$route.params.sumup_group_id;
      await search();
    });

    return {
      ...toRefs(state),
      goBack,
      upload,
      previewImage,
      modalRef,
      pdfRenderAreaRef,
      modalShown,
      modalHidden,
      destroyPdf,
    };
  },
})
